import { IDesignerCanvas } from "../../widgets/designerView/IDesignerCanvas.js";
import { DesignItem } from '../../item/DesignItem.js';
import { IDragDropService } from "./IDragDropService.js";
import { IDesignItem } from "../../item/IDesignItem.js";
import { IPlacementService } from "../placementService/IPlacementService.js";
import { IElementDefinition } from "../elementsService/IElementDefinition.js";
import { ExtensionType } from "../../widgets/designerView/extensions/ExtensionType.js";
import { dragDropFormatNameElementDefinition } from "../../../Constants.js";
import { NpmPackageLoader } from "../../helper/NpmPackageLoader.js";
import { getPossiblePlacementTarget } from "../../helper/PlacementTargetHelper.js";
import { hasCommandKey } from '../../helper/KeyboardHelper.js';

interface IPlacementEnsureAncestor {
  selector: string;
  create?: {
    tag?: string;
    attributes?: Record<string, string>;
    styles?: Record<string, string>;
  };
  mode?: 'wrap';
}

export class DragDropService implements IDragDropService {
  private _dragOverExtensionItem: IDesignItem;
  private _oldX: number;
  private _oldY: number;
  private _currentDragDropFormatNameElementDefinition: string;

  constructor() {
    window.addEventListener("dragstart", (e) => {
      const dt = e.dataTransfer;
      if (!dt) return;
      const origSetData = dt.setData.bind(dt);
      dt.setData = (type, value) => {
        if (type == dragDropFormatNameElementDefinition)
          this._currentDragDropFormatNameElementDefinition = value;
        return origSetData(type, value);
      };
    }, true); // <-- use capture phase!
    window.addEventListener("dragend", (e) => {
      this._dragOverExtensionItem?.instanceServiceContainer.designerCanvas.snapLines.clearSnaplines();
      this._currentDragDropFormatNameElementDefinition = null;
    }, true); // <-- use capture phase!
  }

  public dragEnter(designerCanvas: IDesignerCanvas, event: DragEvent) {
  }

  public dragLeave(designerCanvas: IDesignerCanvas, event: DragEvent) {
    designerCanvas.snapLines.clearSnaplines();
    if (this._dragOverExtensionItem) {
      designerCanvas.extensionManager.removeExtension(this._dragOverExtensionItem, ExtensionType.ContainerExternalDragOverAndCanBeEntered);
      this._dragOverExtensionItem = null;
    }
  }

  public async dragOver(designerCanvas: IDesignerCanvas, event: DragEvent) {
    if (designerCanvas.readOnly) {
      designerCanvas.snapLines.clearSnaplines();
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    let di: IDesignItem = null;
    let transferData = event.dataTransfer.getData(dragDropFormatNameElementDefinition);
    if (!transferData) {
      transferData = this._currentDragDropFormatNameElementDefinition;
    }
    if (transferData) {
      const elementDefinition = <IElementDefinition>JSON.parse(transferData);
      if (elementDefinition) {
        di = await designerCanvas.serviceContainer.forSomeServicesTillResult("instanceService", (service) => service.getElement(elementDefinition, designerCanvas.serviceContainer, designerCanvas.instanceServiceContainer));
      }
    }

    let [newContainer] = this.getPossibleContainerForDragDrop(designerCanvas, event, di ? [di] : null);
    if (!newContainer)
      newContainer = designerCanvas.rootDesignItem;

    if (di)
      this.snapPaletteItem(designerCanvas, event, newContainer, di, JSON.parse(transferData).mouseOffset);

    if (this._dragOverExtensionItem != newContainer) {
      designerCanvas.extensionManager.removeExtension(this._dragOverExtensionItem, ExtensionType.ContainerExternalDragOverAndCanBeEntered);
      designerCanvas.extensionManager.applyExtension(newContainer, ExtensionType.ContainerExternalDragOverAndCanBeEntered, event);
      this._dragOverExtensionItem = newContainer;
    } else {
      if (event.x != this._oldX && event.y != this._oldY) {
        this._oldX = event.x;
        this._oldY = event.y;
        designerCanvas.extensionManager.refreshExtension(newContainer, ExtensionType.ContainerExternalDragOverAndCanBeEntered, event);
      }
    }
  }

  public async drop(designerCanvas: IDesignerCanvas, event: DragEvent) {
    if (this._dragOverExtensionItem) {
      designerCanvas.extensionManager.removeExtension(this._dragOverExtensionItem, ExtensionType.ContainerExternalDragOverAndCanBeEntered);
      this._dragOverExtensionItem = null;
    }

    const transferData = event.dataTransfer.getData(dragDropFormatNameElementDefinition);
    const elementDefinition = <IElementDefinition>JSON.parse(transferData);
    const di = await designerCanvas.serviceContainer.forSomeServicesTillResult("instanceService", (service) => service.getElement(elementDefinition, designerCanvas.serviceContainer, designerCanvas.instanceServiceContainer));

    let [newContainer] = this.getPossibleContainerForDragDrop(designerCanvas, event, [di]);
    if (!newContainer)
      newContainer = designerCanvas.rootDesignItem;

    const grp = di.openGroup("Insert of &lt;" + di.name + "&gt;");
    const placement = this.applyPlacementRules(designerCanvas, newContainer, di, elementDefinition);
    newContainer = placement.container;
    const placedItem = placement.placedItem;

    const containerService = designerCanvas.serviceContainer.getLastServiceWhere('containerService', x => x.serviceForContainer(newContainer, newContainer.getComputedStyle(), placedItem))
    containerService.enterContainer(newContainer, [placedItem], 'drop');

    const containerPos = designerCanvas.getNormalizedElementCoordinates(newContainer.element);
    const evCoord = this.snapPaletteItem(designerCanvas, event, newContainer, placedItem, elementDefinition.mouseOffset);
    designerCanvas.snapLines.clearSnaplines();
    const pos = { x: evCoord.x - containerPos.x, y: evCoord.y - containerPos.y };

    let offset = { x: 0, y: 0 };
    if (elementDefinition.mouseOffset)
      offset = elementDefinition.mouseOffset;
    containerService.place(event, designerCanvas, newContainer, offset, { x: 0, y: 0 }, pos, [placedItem]);
    containerService.finishPlace(event, designerCanvas, newContainer, offset, { x: 0, y: 0 }, pos, [placedItem]);
    requestAnimationFrame(() => {
      designerCanvas.instanceServiceContainer.selectionService.setSelectedElements([di], event);
      grp.commit();
    });
  }

  private snapPaletteItem(designerCanvas: IDesignerCanvas, event: DragEvent, container: IDesignItem, item: IDesignItem, offset = { x: 0, y: 0 }) {
    const point = designerCanvas.getNormalizedEventCoordinates(event);
    designerCanvas.snapLines.clearSnaplines();
    const display = container.getComputedStyle().display;
    if (!designerCanvas.alignOnSnap || designerCanvas.alignOnGrid || hasCommandKey(event) ||
        display === 'flex' || display === 'inline-flex' || display === 'grid' || display === 'inline-grid')
      return point;

    designerCanvas.snapLines.calculateSnaplines([item]);
    const element = item.element as HTMLElement;
    const detached = !element.isConnected;
    const oldStyle = element.style.cssText;
    let rect: DOMRect;
    try {
      if (detached) {
        // Measure intrinsic sizes in the target container without inserting a model item.
        element.style.position = 'absolute';
        element.style.visibility = 'hidden';
        container.usableContainer.appendChild(element);
      }
      rect = element.getBoundingClientRect();
    } finally {
      if (detached) {
        element.remove();
        element.style.cssText = oldStyle;
      }
    }
    const snapped = designerCanvas.snapLines.snapToPosition({ x: point.x - offset.x, y: point.y - offset.y },
      { width: rect.width / designerCanvas.scaleFactor, height: rect.height / designerCanvas.scaleFactor }, { x: 1, y: 1 });
    return { x: snapped.x == null ? point.x : snapped.x + offset.x, y: snapped.y == null ? point.y : snapped.y + offset.y };
  }

  private applyPlacementRules(designerCanvas: IDesignerCanvas, container: IDesignItem, item: IDesignItem, elementDefinition: IElementDefinition): { container: IDesignItem, placedItem: IDesignItem } {
    const packageHack = elementDefinition.packageName ? NpmPackageLoader.getPackageHack(elementDefinition.packageName) : null;
    const rules: IPlacementEnsureAncestor[] = packageHack?.placement?.ensureAncestors;
    if (!rules?.length)
      return { container, placedItem: item };

    let placedItem = item;
    for (const rule of rules) {
      if (rule.mode !== 'wrap' || !rule.selector || this.hasAncestorMatching(container, rule.selector))
        continue;

      const wrapper = this.createWrapperDesignItem(designerCanvas, rule);
      if (!wrapper)
        continue;

      container.insertChild(wrapper);
      container = wrapper;
    }
    return { container, placedItem };
  }

  private hasAncestorMatching(container: IDesignItem, selector: string): boolean {
    let current = container;
    while (current) {
      try {
        if (current.element?.matches(selector))
          return true;
      } catch (err) {
        console.warn('invalid placement ancestor selector: ', selector, err);
        return false;
      }
      current = current.parent;
    }
    return false;
  }

  private createWrapperDesignItem(designerCanvas: IDesignerCanvas, rule: IPlacementEnsureAncestor): IDesignItem {
    if (!rule.create?.tag)
      return null;

    const wrapperElement = designerCanvas.rootDesignItem.document.createElement(rule.create.tag);
    if (rule.create.attributes) {
      for (const name in rule.create.attributes) {
        wrapperElement.setAttribute(name, rule.create.attributes[name]);
      }
    }
    if (rule.create.styles) {
      for (const name in rule.create.styles) {
        wrapperElement.style[name] = rule.create.styles[name];
      }
    }
    return DesignItem.createDesignItemFromInstance(wrapperElement, designerCanvas.serviceContainer, designerCanvas.instanceServiceContainer);
  }

  public getPossibleContainerForDragDrop(designerCanvas: IDesignerCanvas, event: DragEvent, designItems?: IDesignItem[]): [newContainerElementDesignItem: IDesignItem, newContainerService: IPlacementService] {
    return getPossiblePlacementTarget(designerCanvas, event, designItems);
  }
}
